<template>
  <flow-layout :current-step-index="isBrokerFlow ? 3 : 2">
    <heading level="2">{{
      isBrokerFlow ? $t('businessInfo.brokerTitle') : $t('businessInfo.title')
    }}</heading>
    <form autocomplete="off">
      <input-inline>
        <input-select
          id="legal-entity-type"
          class="legalEntityType"
          v-model="business.legalEntityType"
          :defaultValue="$t('select')"
          :options="legalEntityTypeOptions"
          v-validation:closed="[['dynamic', '/business/legalEntityType']]"
          @change="formCleanUp"
        >
          <template #label>{{
            isBrokerFlow
              ? $t('businessInfo.legalEntityType.brokerLabel')
              : $t('businessInfo.legalEntityType.label')
          }}</template>
          <div slot="error">{{ $t('validationMessages.blankInputSelect') }}</div>
        </input-select>
      </input-inline>

      <combined-element v-if="!!business.legalEntityType">
        <template v-if="business.legalEntityType === TRUST">
          <abr-search
            key="business-name-trust"
            id="business-name-trust"
            :value="business.legalBusinessName"
            :placeholder="$t('businessInfo.businessNamePlaceholder')"
            :maxlength="BUSINESS_NAME_MAX_LENGTH"
            :abnTypeLabel="$t('businessInfo.abn.label')"
            :acnTypeLabel="$t('businessInfo.acn.label')"
            :label="$t('businessInfo.trustNameLabel')"
            :acn="false"
            @change="onOptionChange"
            @input="business = { ...business, legalBusinessName: $event }"
            v-validation="[['dynamic', '/business/_extension/trustName']]"
          ></abr-search>
          <input-text-masked
            key="business-abn-trust"
            id="business-abn-trust"
            name="business-abn-trust"
            @input="checkFormValidation"
            v-model="business.abnTaxId"
            class="halved"
            :mask="[
              /\d/,
              /\d/,
              ' ',
              /\d/,
              /\d/,
              /\d/,
              ' ',
              /\d/,
              /\d/,
              /\d/,
              ' ',
              /\d/,
              /\d/,
              /\d/,
            ]"
            :cleanup-pattern="/[-|\s|(|)]/g"
            :placeholder="$t('businessInfo.abn.placeholder')"
            :maxlength="ABN_MAX_LENGTH"
            :strict-mode="{ regex: /[^0-9]/g, skipDefault: true }"
            v-validation="[['dynamic', '/business/_extension/trustAbn']]"
            ref="abnTaxId"
          >
            <template #label>
              {{ $t('businessInfo.abn.trustLabel') }}
              <popover small class="popover" svgName="Icons _ 01 System _ Operations _ Tooltip">
                <i18n
                  :path="
                    isBrokerFlow
                      ? 'businessInfo.abn.brokerTrustTooltip'
                      : 'businessInfo.abn.trustTooltip'
                  "
                >
                  <brand-link
                    class="descriptionLink"
                    target="_blank"
                    :href="isBrokerFlow ? acnLink : abnLink"
                    slot="link"
                    >{{
                      isBrokerFlow
                        ? $t('businessInfo.acn.linkText')
                        : $t('businessInfo.abn.linkText')
                    }}</brand-link
                  >
                </i18n>
              </popover>
            </template>
          </input-text-masked>

          <input-text
            key="trust-trading-name"
            id="trust-trading-name"
            name="trust-trading-name"
            @input="checkFormValidation"
            v-model="business.doingBusinessAs"
            :maxlength="DOING_BUSINESS_AS_MAX_LENGTH"
            v-validation="[['dynamic', '/business/doingBusinessAs']]"
          >
            <template #label>{{ $t('businessInfo.trustDoingBusinessAs.label') }}</template>
            <template #description>
              {{ $t('businessInfo.trustDoingBusinessAs.suffix') }}
            </template>
          </input-text>

          <input-inline>
            <input-select
              id="trustee-type"
              class="trusteeType"
              ref="trusteeType"
              v-model="business._extension.trusteeType"
              :defaultValue="$t('select')"
              :options="trusteeTypeOptions"
              v-validation:closed="[['dynamic', '/business/_extension/trusteeType']]"
              @change="trusteeFormCleanUp"
            >
              <template #label>{{ $t('businessInfo.trusteeType.label') }}</template>
            </input-select>
          </input-inline>

          <combined-element
            class="deepCombined"
            v-if="business._extension.trusteeType === TRUSTEE_COMPANY"
          >
            <abr-search
              key="trustee-name-company"
              id="trustee-name-company"
              :value="_extension.trusteeName"
              :placeholder="$t('businessInfo.businessNamePlaceholder')"
              :maxlength="TRUSTEE_NAME_MAX_LENGTH"
              :abnTypeLabel="$t('businessInfo.abn.trusteeLabel')"
              :acnTypeLabel="$t('businessInfo.acn.trusteeLabel')"
              :label="$t('businessInfo.trusteeNameLabel.COMPANY')"
              :acn="true"
              @change="onOptionChangeAcnTrustee"
              @input="_extension = { ..._extension, trusteeName: $event }"
              v-validation="[['dynamic', '/business/_extension/trusteeName']]"
            ></abr-search>
            <input-text-masked
              key="trustee-acn"
              id="trustee-acn"
              name="trustee-acn"
              class="halved"
              @input="checkFormValidation"
              v-model="business._extension.trusteeTaxId"
              :mask="[/\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/]"
              :cleanup-pattern="/[-|\s|(|)]/g"
              :placeholder="$t('businessInfo.acn.placeholder')"
              :maxlength="ACN_MAX_LENGTH"
              :strict-mode="{ regex: /[^0-9]/g, skipDefault: true }"
              v-validation="[['dynamic', '/business/_extension/trusteeAcn']]"
              ref="trusteeTaxId"
            >
              <template #label>
                {{ $t('businessInfo.acn.trusteeLabel') }}
                <popover small class="popover" svgName="Icons _ 01 System _ Operations _ Tooltip">
                  <i18n path="businessInfo.acn.trustTooltip">
                    <brand-link
                      class="descriptionLink"
                      target="_blank"
                      :href="acnLink"
                      slot="link"
                      >{{ $t('businessInfo.acn.linkText') }}</brand-link
                    >
                  </i18n>
                </popover>
              </template>
            </input-text-masked>
            <input-text
              key="trustee-another-business-name"
              id="trustee-another-business-name"
              name="trustee-another-business-name"
              @input="checkFormValidation"
              ref="trusteeAnotherBusinessName"
              v-model="business._extension.trusteeDoingBusinessAs"
              :maxlength="DOING_BUSINESS_AS_MAX_LENGTH"
              v-validation="[['dynamic', '/business/_extension/trusteeDoingBusinessAs']]"
            >
              <template #label>{{ $t('businessInfo.trusteeDoingBusinessAs.label') }}</template>
              <template #description>
                {{ $t('businessInfo.trusteeDoingBusinessAs.suffix') }}
              </template>
            </input-text>
          </combined-element>
        </template>

        <template v-else-if="business.legalEntityType === COMPANY">
          <abr-search
            key="business-name-company"
            id="business-name-company"
            :value="business.legalBusinessName"
            :placeholder="$t('businessInfo.businessNamePlaceholder')"
            :acn="true"
            :abnTypeLabel="$t('businessInfo.abn.label')"
            :acnTypeLabel="$t('businessInfo.acn.label')"
            :label="$t('businessInfo.businessNameLabel')"
            :maxlength="BUSINESS_NAME_MAX_LENGTH"
            @change="onOptionChangeAcn"
            @input="business = { ...business, legalBusinessName: $event }"
            v-validation="[['dynamic', '/business/legalBusinessName']]"
          ></abr-search>
          <input-inline>
            <input-text-masked
              key="business-acn"
              id="business-acn"
              @input="checkFormValidation"
              name="business-acn"
              v-model="business.taxId"
              :mask="[/\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/]"
              :cleanup-pattern="/[-|\s|(|)]/g"
              :placeholder="$t('businessInfo.acn.placeholder')"
              :maxlength="ACN_MAX_LENGTH"
              :strict-mode="{ regex: /[^0-9]/g, skipDefault: true }"
              v-validation="[['dynamic', '/business/_extension/acn']]"
              ref="taxId"
            >
              <template #label>
                {{ $t('businessInfo.acn.label') }}
                <popover small class="popover" svgName="Icons _ 01 System _ Operations _ Tooltip">
                  <i18n
                    :path="
                      isBrokerFlow ? 'businessInfo.acn.brokerTooltip' : 'businessInfo.acn.tooltip'
                    "
                  >
                    <brand-link
                      class="descriptionLink"
                      target="_blank"
                      :href="acnLink"
                      slot="link"
                      >{{ $t('businessInfo.acn.linkText') }}</brand-link
                    >
                  </i18n>
                </popover>
              </template>
            </input-text-masked>
          </input-inline>
        </template>

        <template v-else>
          <abr-search
            :key="`business-name-${business.legalEntityType}`"
            :id="`business-name-${business.legalEntityType}`"
            :value="business.legalBusinessName"
            :placeholder="$t('businessInfo.businessNamePlaceholder')"
            :acn="false"
            :abnTypeLabel="$t('businessInfo.abn.label')"
            :acnTypeLabel="$t('businessInfo.acn.label')"
            :label="$t('businessInfo.businessNameLabel')"
            :maxlength="BUSINESS_NAME_MAX_LENGTH"
            @change="onOptionChange"
            @input="business = { ...business, legalBusinessName: $event }"
            v-validation="[['dynamic', '/business/legalBusinessName']]"
          ></abr-search>
          <input-inline>
            <input-text-masked
              key="business-abn"
              id="business-abn"
              name="business-abn"
              @input="checkFormValidation"
              v-model="business.abnTaxId"
              :mask="[
                /\d/,
                /\d/,
                ' ',
                /\d/,
                /\d/,
                /\d/,
                ' ',
                /\d/,
                /\d/,
                /\d/,
                ' ',
                /\d/,
                /\d/,
                /\d/,
              ]"
              :cleanup-pattern="/[-|\s|(|)]/g"
              :placeholder="$t('businessInfo.abn.placeholder')"
              :maxlength="ABN_MAX_LENGTH"
              :strict-mode="{ regex: /[^0-9]/g, skipDefault: true }"
              v-validation="[['dynamic', '/business/_extension/abn']]"
              ref="abnTaxId"
            >
              <template #label>
                {{ $t('businessInfo.abn.label') }}
                <popover small class="popover" svgName="Icons _ 01 System _ Operations _ Tooltip">
                  <i18n
                    :path="
                      isBrokerFlow ? 'businessInfo.abn.brokerTooltip' : 'businessInfo.abn.tooltip'
                    "
                  >
                    <brand-link
                      class="descriptionLink"
                      target="_blank"
                      :href="isBrokerFlow ? acnLink : abnLink"
                      slot="link"
                      >{{
                        isBrokerFlow
                          ? $t('businessInfo.acn.linkText')
                          : $t('businessInfo.abn.linkText')
                      }}</brand-link
                    >
                  </i18n>
                </popover>
              </template>
            </input-text-masked>
          </input-inline>
        </template>
        <input-text
          v-if="business.legalEntityType !== TRUST"
          key="another-business-name"
          id="another-business-name"
          @input="checkFormValidation"
          name="another-business-name"
          ref="anotherBusinessName"
          v-model="business.doingBusinessAs"
          :maxlength="DOING_BUSINESS_AS_MAX_LENGTH"
          v-validation="[['dynamic', '/business/doingBusinessAs']]"
        >
          <template #label>{{ $t('businessInfo.doingBusinessAsLabel.label') }}</template>
          <div slot="error">
            {{
              $t('validationMessages.wrongFieldValue', {
                fieldName: $t('businessInfo.doingBusinessAsLabel.label'),
              })
            }}
          </div>
          <template #description>
            {{ $t('businessInfo.doingBusinessAsLabel.suffix') }}
          </template>
        </input-text>
      </combined-element>

      <hr class="line" />

      <address-autocomplete
        id="business-address"
        ref="businessAddress"
        labelPathReference="businessInfo"
        @input="checkFormValidation"
        v-model="business"
        validationPathPrefix="/business"
        :maxlength="ADDRESS_MAX_LENGTH"
        :postalCodePlaceholder="$t('postalCodePlaceholder')"
        :stateDefaultValue="$t('select')"
        @autocomplete="onPlaceChange"
      />

      <input-inline>
        <input-text-masked
          id="business-phone"
          ref="businessPhone"
          name="business-phone"
          class="businessPhone"
          @input="checkFormValidation"
          v-model="business.phone"
          :mask="phoneMask"
          :cleanup-pattern="/[-|\s|(|)]/g"
          :placeholder-char="'\u2000'"
          :guide="false"
          v-validation="[['dynamic', '/business/phone']]"
          :maxlength="PHONE_MAX_LENGTH"
        >
          <template #label>{{ $t('businessInfo.phoneFieldLabel') }}</template>
        </input-text-masked>
      </input-inline>

      <navigation-buttons
        @next="submit"
        :next="{
          label: $t('nextButtonLabel'),
          modifiers: { disabled: !isFormValid },
        }"
        @back="goBack"
      />
    </form>
  </flow-layout>
</template>
<script>
import abrSearch from '@/components/abrSearch';
import FlowLayout from '@/views/layouts/FlowLayout';
import InputInline from '@/components/InputInline';
import CombinedElement from '@/components/CombinedElement';
import AddressAutocomplete from '@/components/AddressAutocomplete';

import applicationMixin from '@/mixins/application';
import brokerFlowMixin from '@/mixins/broker-flow';
import states from '@/modules/states';
import legalEntityTypes, { COMPANY, TRUST } from '@/modules/legal-entity-types';
import trusteeTypes, {
  COMPANY as TRUSTEE_COMPANY,
  PARTNERSHIP as TRUSTEE_PARTNERSHIP,
} from '@/modules/trustee-types';

import mapConstsToOptions from '@/utils/map-consts-to-options';

import {
  BUSINESS_NAME_MAX_LENGTH,
  ACN_MAX_LENGTH,
  ABN_MAX_LENGTH,
  DOING_BUSINESS_AS_MAX_LENGTH,
  TRUSTEE_NAME_MAX_LENGTH,
  ADDRESS_MAX_LENGTH,
  CITY_MAX_LENGTH,
  POSTAL_CODE_MAX_LENGTH,
  PHONE_MAX_LENGTH,
} from '@/modules/validation-service/fields-length';
import { getPhoneMask } from '@/modules/field-mask';

export default {
  name: 'business-info',
  components: {
    abrSearch,
    FlowLayout,
    InputInline,
    CombinedElement,
    AddressAutocomplete,
  },
  data() {
    return {
      abnLink: this.$env.VUE_APP_ABN_URL,
      acnLink: this.$env.VUE_APP_ACN_URL,
      businessNameOptions: [],
      trusteeNameOptions: [],

      COMPANY,
      TRUST,
      TRUSTEE_COMPANY,
      TRUSTEE_PARTNERSHIP,

      BUSINESS_NAME_MAX_LENGTH,
      ACN_MAX_LENGTH,
      ABN_MAX_LENGTH,
      DOING_BUSINESS_AS_MAX_LENGTH,
      TRUSTEE_NAME_MAX_LENGTH,
      ADDRESS_MAX_LENGTH,
      CITY_MAX_LENGTH,
      POSTAL_CODE_MAX_LENGTH,
      PHONE_MAX_LENGTH,
      isFormValid: false,
    };
  },
  computed: {
    business: {
      get() {
        return this.application.business;
      },
      set(value) {
        this.application = { ...this.application, business: value };
      },
    },
    _extension: {
      get() {
        return this.business._extension;
      },
      set(value) {
        this.business = { ...this.business, _extension: value };
      },
    },
    legalEntityTypeOptions() {
      return mapConstsToOptions(
        Object.values(legalEntityTypes),
        'businessInfo.legalEntityType.options',
      );
    },
    trusteeTypeOptions() {
      return mapConstsToOptions(Object.values(trusteeTypes), 'businessInfo.trusteeType.options');
    },
    stateOptions() {
      return mapConstsToOptions(states, 'states');
    },
    phoneMask() {
      return getPhoneMask(this.business.phone);
    },
  },
  methods: {
    checkFormValidation() {
      this.$nextTick().then(() => {
        this.isFormValidFn();
      });
    },
    isFormValidFn() {
      let validationCounter = 0;
      this.isFormValid = false;
      this.$validations.collections[0].forEach((value, key) => {
        if (
          key === 'trust-trading-name' ||
          key === 'trustee-another-business-name' ||
          key === 'another-business-name' ||
          key === 'business-phone' ||
          key === 'business-address-unit-number'
        ) {
          validationCounter += 1;
        } else if (
          value.component.value &&
          (value.component.errorMessage === '' || value.component.errorMessage === undefined)
        ) {
          validationCounter += 1;
        } else if (
          value.component.empty === false &&
          (value.component.errorMessage === '' || value.component.errorMessage === undefined)
        ) {
          validationCounter += 1;
        }
      });
      this.isFormValid = validationCounter === this.$validations.collections[0].size;
    },
    formCleanUp() {
      this.business.taxId = undefined;
      this.business.abnTaxId = undefined;
      this.business.legalBusinessName = undefined;
      this.business.doingBusinessAs = undefined;
      this.business._extension.trusteeTaxId = undefined;
      this.business._extension.trusteeDoingBusinessAs = undefined;
      this.business._extension.trusteeName = undefined;
      this.checkFormValidation();
    },
    trusteeFormCleanUp() {
      this.business._extension.trusteeTaxId = undefined;
      this.business._extension.trusteeDoingBusinessAs = undefined;
      this.business._extension.trusteeName = undefined;
      this.checkFormValidation();
    },
    setFocusTo(element) {
      if (element) {
        this.$nextTick().then(() => {
          element.focus();
        });
      }
    },
    onOptionChange(option) {
      this.business = {
        ...this.business,
        legalBusinessName: option.value,
        abnTaxId: option.abn,
      };
      this.$refs.taxId.setErrorMessage();

      this.setFocusTo(
        this.business.legalEntityType === TRUST
          ? this.$refs.trusteeType.$refs.trigger
          : this.$refs.anotherBusinessName.$refs.nativeInput,
      );
      this.checkFormValidation();
    },
    onOptionChangeAcn(option) {
      this.business = {
        ...this.business,
        legalBusinessName: option.value,
        taxId: option.acn,
        abnTaxId: option.abn,
      };
      this.$refs.taxId.setErrorMessage();

      this.setFocusTo(this.$refs.anotherBusinessName.$refs.nativeInput);
      this.checkFormValidation();
    },
    onOptionChangeTrustee(option) {
      this.business = {
        ...this.business,
        _extension: {
          ...this.business._extension,
          trusteeName: option.value,
          trusteeTaxId: option.abn,
        },
      };
      this.$refs.trusteeTaxId.setErrorMessage();

      this.setFocusTo(this.$refs.trusteeAnotherBusinessName.$refs.nativeInput);
      this.checkFormValidation();
    },
    onOptionChangeAcnTrustee(option) {
      this.business = {
        ...this.business,
        _extension: {
          ...this.business._extension,
          trusteeName: option.value,
          trusteeTaxId: option.acn,
        },
      };
      this.$refs.trusteeTaxId.setErrorMessage();

      this.setFocusTo(this.$refs.trusteeAnotherBusinessName.$refs.nativeInput);
      this.checkFormValidation();
    },
    onPlaceChange() {
      this.setFocusTo(this.$refs.businessPhone.$refs.maskedInput.$refs.input);
    },
  },
  mixins: [applicationMixin, brokerFlowMixin],
};
</script>
<style lang="sass" scoped>
::v-deep
  .inputInline
    gap: $glass-spacing-3
  .halved .control
    +mq($stego)
      width: 48.5%

.line
  background: $color-system-neutral-gray-3
  border: none
  width: 100%
  height: baseunit(0.1)
  margin: $glass-spacing-4 0 $glass-spacing-2

.combinedElement
  padding: $glass-spacing-0125 $glass-spacing-3 $glass-spacing-4
  background-color: $glass-color-system-neutral-grey-02

  .deepCombined
    background-color: $glass-color-system-neutral-grey-03

.descriptionLink
  font-weight: bold
  text-decoration: underline
  color: #000000

.popover
  vertical-align: super
  ::v-deep
    .icon
      color: $glass-color-brand-primary-dark-03
    .content
      background: $glass-color-brand-secondary-accent-01
      color: #000000
      z-index: 2
    .iconButton
      &:focus
        outline: 0
        box-shadow: 0 0 0 4px $glass-color-brand-primary-light-02
</style>
